import React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { getInitials } from '../../../utils';
import { detailCardSx } from './detailSection.component';

interface DetailHeaderProps {
  /** Entity-type icon shown in the avatar. */
  icon?: React.ReactNode;
  /** Name to derive avatar initials from - used for people instead of an icon. */
  avatarText?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Status and other chips, shown at the end of the header line. */
  chips?: React.ReactNode;
  /** Extra content under the header line, e.g. an alert about the entity's state. */
  children?: React.ReactNode;
}

/** The identity card at the top of a detail view. */
export const DetailHeader = ({ icon, avatarText, title, subtitle, chips, children }: DetailHeaderProps) => (
  <Box sx={{ ...detailCardSx, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {(icon || avatarText) && (
        <Avatar
          sx={(theme) => ({
            width: 48,
            height: 48,
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.12),
            color: 'primary.main',
            fontWeight: 700,
            fontSize: '1.1rem',
          })}
        >
          {icon ?? getInitials(avatarText)}
        </Avatar>
      )}
      <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.25, wordBreak: 'break-word' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {chips && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {chips}
        </Box>
      )}
    </Box>
    {children}
  </Box>
);
